import { useEffect, useRef, useState } from "react";
import { adminService } from "../../../features/admin/service";
import { parseApiError } from "../../../lib/http/apiErrors";
import type {
  AdminTemplate,
  AdminTemplateCategory,
  AdminTemplateField,
  AdminTemplateFromAssetDraft,
} from "../../../features/admin/types";

type AssetType = "flyer" | "video" | "itinerary";

const inputCls =
  "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 placeholder-gray-500";
const labelCls = "block text-xs text-gray-400 mb-1";

const FLYER_STYLE_FIELDS = ["style", "mood", "color_tone", "flyer_type", "overlay_style", "time_of_day"] as const;
const VIDEO_STYLE_FIELDS = [
  "mood", "camera_movement", "motion_style", "transition_style",
  "text_animation_style", "audio_mood", "scene_structure", "video_duration",
] as const;

const FIELD_TYPE_OPTIONS = ["text", "textarea", "number", "date", "select"];

interface FormState {
  name: string;
  category_slug: string;
  base_prompt: string;
  prompt_template: string;
  aspect_ratio: string;
  cta_text: string;
  tagline: string;
  styling: Record<string, string>;
  ask_aspect_ratio: boolean;
  ask_duration: boolean;
  html_template_id: string;
  html_template_content: string;
  render_engine: string;
  fields: AdminTemplateField[];
}

function emptyField(): AdminTemplateField {
  return { key: "", label: "", placeholder: "", required: false, field_type: "text" };
}

function buildInitialState(
  assetType: AssetType,
  template?: AdminTemplate | null,
  draft?: AdminTemplateFromAssetDraft | null,
): FormState {
  const styling: Record<string, string> = {};
  const styleFields = assetType === "video" ? VIDEO_STYLE_FIELDS : FLYER_STYLE_FIELDS;

  if (template) {
    styleFields.forEach((k) => {
      const v = (template as Record<string, any>)[k];
      styling[k] = v == null ? "" : String(v);
    });
  } else if (draft?.styling) {
    styleFields.forEach((k) => {
      const v = draft.styling?.[k];
      styling[k] = v == null ? "" : String(v);
    });
  } else {
    styleFields.forEach((k) => { styling[k] = ""; });
  }

  let fields: AdminTemplateField[] = [];
  if (template?.fields?.length) {
    fields = template.fields.map((f) => ({
      key: f.key ?? "",
      label: f.label ?? "",
      placeholder: f.placeholder ?? "",
      required: !!f.required,
      field_type: f.field_type ?? "text",
    }));
  } else if (draft?.fields_json) {
    try {
      const parsed = JSON.parse(draft.fields_json);
      if (Array.isArray(parsed)) {
        fields = parsed.map((f: any) => ({
          key: f.key ?? "",
          label: f.label ?? "",
          placeholder: f.placeholder ?? "",
          required: !!f.required,
          field_type: f.field_type ?? "text",
        }));
      }
    } catch { /* ignore */ }
  }

  return {
    name: template?.name ?? draft?.name ?? "",
    category_slug: template?.category ?? draft?.category_slug ?? "",
    base_prompt: template?.base_prompt ?? draft?.base_prompt ?? "",
    prompt_template: template?.prompt_template ?? draft?.prompt_template ?? "",
    aspect_ratio: template?.aspect_ratio ?? "9:16",
    cta_text: template?.cta_text ?? "",
    tagline: template?.tagline ?? "",
    styling,
    ask_aspect_ratio: template?.ask_aspect_ratio ?? false,
    ask_duration: template?.ask_duration ?? false,
    html_template_id: template?.html_template_id ?? "",
    html_template_content: template?.html_template_content ?? "",
    // New templates default to v2; existing ones keep their stored engine
    // (absent → legacy) so editing an old template never silently flips it.
    render_engine: template
      ? ((template as Record<string, any>).render_engine ?? "legacy")
      : "v2",
    fields,
  };
}

interface Props {
  assetType: AssetType;
  template?: AdminTemplate | null;
  draft?: AdminTemplateFromAssetDraft | null;
  categories: AdminTemplateCategory[];
  onClose: () => void;
  onSaved: () => void;
}

export default function TemplateFormModal({ assetType, template, draft, categories, onClose, onSaved }: Props) {
  const isEdit = !!template;
  const [form, setForm] = useState<FormState>(() => buildInitialState(assetType, template, draft));
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [samplePdf, setSamplePdf] = useState<File | null>(null);
  const [htmlFile, setHtmlFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [generatingStatusText, setGeneratingStatusText] = useState("");
  const [planTimeline, setPlanTimeline] = useState<{ scenes: Array<{ id: string; scene_prompt: string }> } | null>(null);
  const [generatedVideoS3Key, setGeneratedVideoS3Key] = useState<string | null>(null);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const [generatedPreviewS3Key, setGeneratedPreviewS3Key] = useState<string | null>(null);
  const [lintWarnings, setLintWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const unmountedRef = useRef(false);
  useEffect(() => {
    // Reset on (re)mount, not just cleanup — StrictMode's dev-only
    // mount→unmount→remount cycle otherwise leaves this stuck `true`
    // forever after the first mount, since the cleanup below runs once
    // during that simulated unmount and nothing ever flips it back.
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  const styleFields = assetType === "video" ? VIDEO_STYLE_FIELDS : FLYER_STYLE_FIELDS;
  const existingPreview = template?.preview_image_url ?? draft?.preview_image_url ?? null;
  const prefillPreviewS3 = !template ? draft?.preview_s3_key ?? null : null;
  const prefillVideoS3 = !template ? draft?.video_s3_key ?? null : null;

  useEffect(() => {
    setForm(buildInitialState(assetType, template, draft));
  }, [assetType, template, draft]);

  const setStyling = (key: string, value: string) =>
    setForm((f) => ({ ...f, styling: { ...f.styling, [key]: value } }));

  const updateField = (idx: number, patch: Partial<AdminTemplateField>) =>
    setForm((f) => ({ ...f, fields: f.fields.map((fl, i) => (i === idx ? { ...fl, ...patch } : fl)) }));

  const addField = () => setForm((f) => ({ ...f, fields: [...f.fields, emptyField()] }));
  const removeField = (idx: number) =>
    setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));

  const handleExtractHtml = async () => {
    const html = form.html_template_content.trim();
    if (!html) { setError("Paste the layout HTML first, then extract."); return; }
    setExtracting(true);
    setError(null);
    setLintWarnings([]);
    try {
      const result = await adminService.extractItineraryHtml(html);
      setForm((f) => ({
        ...f,
        base_prompt: result.base_prompt || f.base_prompt,
        prompt_template: result.prompt_template || f.prompt_template,
        fields: result.fields?.length
          ? result.fields.map((fld) => ({
              key: fld.key ?? "",
              label: fld.label ?? "",
              placeholder: fld.placeholder ?? "",
              required: !!fld.required,
              field_type: fld.field_type ?? "text",
            }))
          : f.fields,
      }));
      setLintWarnings(result.lint_warnings ?? []);
    } catch (err) {
      setError("HTML extraction failed. You can fill fields manually.");
    } finally {
      setExtracting(false);
    }
  };

  const durationSeconds = Number(form.styling.video_duration) || undefined;
  const canGeneratePreview = Boolean(form.base_prompt.trim() && form.aspect_ratio.trim() && durationSeconds);

  const handlePlanPreview = async () => {
    if (!form.base_prompt.trim() || !form.aspect_ratio.trim() || !durationSeconds) {
      setError("Add a Base Prompt, Aspect Ratio, and Duration first — Generate Preview needs all three.");
      return;
    }
    setGeneratingPreview(true);
    setError(null);
    try {
      const result = await adminService.planVideoTemplatePreview(form.base_prompt.trim(), {
        aspect_ratio: form.aspect_ratio,
        duration_hint_seconds: durationSeconds,
      });
      setPlanTimeline(result.timeline);
    } catch (err) {
      setError(parseApiError(err).message ?? "Failed to plan preview video.");
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleConfirmGeneratePreview = async () => {
    if (!planTimeline) return;
    setGeneratingPreview(true);
    setGeneratingStatusText("Starting…");
    setError(null);
    try {
      const started = await adminService.startVideoTemplatePreview(planTimeline);
      // Real generation has taken 6+ minutes — poll instead of one long
      // request, since a single connection that long can get killed by a
      // reverse proxy's read timeout before the answer comes back, even
      // though the video itself finished fine.
      let elapsedSeconds = 0;
      while (!unmountedRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 4000));
        elapsedSeconds += 4;
        const poll = await adminService.getVideoTemplatePreviewStatus(started.preview_id);
        if (unmountedRef.current) return;
        if (poll.status === "completed") {
          setGeneratedVideoS3Key(poll.video_s3_key);
          setGeneratedVideoUrl(poll.video_url);
          setGeneratedPreviewS3Key(poll.preview_s3_key || null);
          setVideoFile(null); // generated preview takes priority over any earlier manual pick
          setPlanTimeline(null);
          return;
        }
        if (poll.status === "failed") {
          setError(poll.error || "Failed to generate preview video.");
          return;
        }
        setGeneratingStatusText(`Generating video… (${elapsedSeconds}s elapsed)`);
      }
    } catch (err) {
      if (!unmountedRef.current) setError(parseApiError(err).message ?? "Failed to generate preview video.");
    } finally {
      if (!unmountedRef.current) { setGeneratingPreview(false); setGeneratingStatusText(""); }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Name is required."); return; }
    if (!form.category_slug.trim()) { setError("Category is required."); return; }
    if (assetType === "itinerary" && !htmlFile && !form.html_template_content.trim() && !isEdit) { setError("Provide an HTML template — upload a .html file or paste the layout HTML."); return; }
    setSaving(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("category_slug", form.category_slug);
      fd.append("name", form.name);
      fd.append("base_prompt", form.base_prompt);
      fd.append("prompt_template", form.prompt_template);
      fd.append("cta_text", form.cta_text);
      fd.append("tagline", form.tagline);
      fd.append("aspect_ratio", form.aspect_ratio);
      fd.append(
        "fields_json",
        JSON.stringify(
          form.fields
            .filter((f) => f.key.trim())
            .map((f) => ({
              key: f.key.trim(),
              label: f.label.trim(),
              placeholder: f.placeholder.trim(),
              required: f.required,
              field_type: f.field_type || "text",
            })),
        ),
      );

      // Type-specific styling fields (flyer/video only — itinerary ignores them)
      if (assetType !== "itinerary") {
        styleFields.forEach((k) => fd.append(k, form.styling[k] ?? ""));
      }
      if (assetType === "video") {
        fd.append("ask_aspect_ratio", form.ask_aspect_ratio ? "true" : "false");
        fd.append("ask_duration", form.ask_duration ? "true" : "false");
      }
      if (assetType === "itinerary") fd.append("html_template_id", form.html_template_id);

      // Files / prefilled S3 keys
      if (previewFile) fd.append("preview_image", previewFile);
      else if (generatedPreviewS3Key) fd.append("preview_s3_key", generatedPreviewS3Key);
      else if (prefillPreviewS3) fd.append("preview_s3_key", prefillPreviewS3);

      if (assetType === "video") {
        if (videoFile) fd.append("template_video", videoFile);
        else if (generatedVideoS3Key) fd.append("video_s3_key", generatedVideoS3Key);
        else if (prefillVideoS3) fd.append("video_s3_key", prefillVideoS3);
      }
      if (assetType === "itinerary") {
        if (samplePdf) fd.append("sample_pdf", samplePdf);
        if (htmlFile) fd.append("html_template_file", htmlFile);
        fd.append("html_template_content", form.html_template_content);
        fd.append("render_engine", form.render_engine || "legacy");
      }

      if (isEdit && template) {
        await adminService.updateAdminTemplate(assetType, template.id, fd);
      } else {
        await adminService.createAdminTemplate(assetType, fd);
      }
      onSaved();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = (k: string) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl shadow-2xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-gray-900 rounded-t-2xl z-10">
          <h2 className="text-lg font-semibold text-white">
            {isEdit ? "Edit" : "New"} {assetType.charAt(0).toUpperCase() + assetType.slice(1)} Template
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="p-6 space-y-4">
          {error && <div className="px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Category *</label>
              <select
                required
                value={form.category_slug}
                onChange={(e) => setForm((f) => ({ ...f, category_slug: e.target.value }))}
                className={inputCls}
              >
                <option value="" disabled>Select a category</option>
                {form.category_slug && !categories.some((c) => c.slug === form.category_slug) && (
                  <option value={form.category_slug}>{form.category_slug}</option>
                )}
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Base Prompt</label>
            <textarea rows={3} value={form.base_prompt} onChange={(e) => setForm((f) => ({ ...f, base_prompt: e.target.value }))} className={inputCls} />
            {assetType === "video" && !planTimeline && (
              <button
                type="button"
                onClick={() => void handlePlanPreview()}
                disabled={generatingPreview || !canGeneratePreview}
                className="mt-2 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-xs font-medium"
                title={canGeneratePreview ? "Plan the scene-by-scene prompt via Video_generator_agent — review before any footage is generated" : "Fill in Base Prompt, Aspect Ratio, and Duration first"}
              >
                {generatingPreview ? "Planning scenes…" : "🎬 Generate Preview"}
              </button>
            )}
            {assetType === "video" && planTimeline && (
              <div className="mt-3 rounded-lg border border-indigo-700/40 bg-indigo-900/10 p-3">
                <p className="text-xs font-semibold text-indigo-300 mb-2">
                  Revised scene prompt — review before generating footage:
                </p>
                <div className="space-y-2 mb-3">
                  {planTimeline.scenes.map((s, i) => (
                    <div key={s.id ?? i} className="text-xs text-gray-300">
                      <span className="text-gray-500">Scene {i + 1}:</span> {s.scene_prompt}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handleConfirmGeneratePreview()}
                    disabled={generatingPreview}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs font-medium"
                  >
                    {generatingPreview ? (generatingStatusText || "Generating video…") : "✓ Confirm & Generate Video"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlanTimeline(null)}
                    disabled={generatingPreview}
                    className="px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-300 text-xs font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className={labelCls}>Prompt Template</label>
            <textarea rows={3} value={form.prompt_template} onChange={(e) => setForm((f) => ({ ...f, prompt_template: e.target.value }))} className={inputCls} />
          </div>

          {/* CTA Text / Tagline hidden for video — video no longer bakes
              AI-written on-screen text (sellers add their own text afterward
              via the Edit Text overlay editor), so these have nothing left to
              feed. Flyer/itinerary still use them. */}
          <div className={`grid ${assetType === "video" ? "grid-cols-3" : assetType === "itinerary" ? "grid-cols-2" : "grid-cols-3"} gap-4`}>
            {assetType !== "itinerary" && (
              <div>
                <label className={labelCls}>Aspect Ratio</label>
                <input value={form.aspect_ratio} onChange={(e) => setForm((f) => ({ ...f, aspect_ratio: e.target.value }))} placeholder="9:16" className={inputCls} />
              </div>
            )}
            {assetType === "video" && (
              <div>
                <label className={labelCls}>Duration (seconds)</label>
                <input value={form.styling.video_duration ?? ""} onChange={(e) => setStyling("video_duration", e.target.value)} placeholder="10" className={inputCls} />
              </div>
            )}
            {assetType !== "video" && (
              <>
                <div>
                  <label className={labelCls}>CTA Text</label>
                  <input value={form.cta_text} onChange={(e) => setForm((f) => ({ ...f, cta_text: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Tagline</label>
                  <input value={form.tagline} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))} className={inputCls} />
                </div>
              </>
            )}
          </div>

          {/* Type-specific styling — flyer only. Video styling now lives
              entirely in Base Prompt + Aspect Ratio + Duration above (see
              Generate Preview). Itineraries take colour/mood from the user
              at generation time, never from the template. */}
          {assetType === "flyer" ? (
            <div className="border-t border-gray-800 pt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Flyer Styling</h3>
              <div className="grid grid-cols-3 gap-4">
                {styleFields.map((k) => (
                  <div key={k}>
                    <label className={labelCls}>{fieldLabel(k)}</label>
                    <input value={form.styling[k] ?? ""} onChange={(e) => setStyling(k, e.target.value)} className={inputCls} />
                  </div>
                ))}
              </div>
            </div>
          ) : assetType === "itinerary" ? (
            <div className="border-t border-gray-800 pt-4">
              <label className={labelCls}>HTML Template ID</label>
              <input value={form.html_template_id} onChange={(e) => setForm((f) => ({ ...f, html_template_id: e.target.value }))} className={inputCls} />
            </div>
          ) : null}

          {/* Fields editor */}
          <div className="border-t border-gray-800 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Fields</h3>
              <button type="button" onClick={addField} className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300">+ Add Field</button>
            </div>
            <div className="space-y-2">
              {form.fields.length === 0 && <p className="text-xs text-gray-600">No fields. Add one to expose user inputs.</p>}
              {form.fields.map((fl, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input value={fl.key} onChange={(e) => updateField(idx, { key: e.target.value })} placeholder="key" className={`col-span-2 ${inputCls}`} />
                  <input value={fl.label} onChange={(e) => updateField(idx, { label: e.target.value })} placeholder="label" className={`col-span-3 ${inputCls}`} />
                  <input value={fl.placeholder} onChange={(e) => updateField(idx, { placeholder: e.target.value })} placeholder="placeholder" className={`col-span-3 ${inputCls}`} />
                  <select value={fl.field_type} onChange={(e) => updateField(idx, { field_type: e.target.value })} className={`col-span-2 ${inputCls}`}>
                    {FIELD_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <label className="col-span-1 flex items-center justify-center text-xs text-gray-400">
                    <input type="checkbox" checked={fl.required} onChange={(e) => updateField(idx, { required: e.target.checked })} className="w-4 h-4 accent-indigo-500" />
                  </label>
                  <button type="button" onClick={() => removeField(idx)} className="col-span-1 text-red-400 hover:text-red-300 text-sm">✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* File uploads */}
          <div className="border-t border-gray-800 pt-4 space-y-3">
            {assetType === "itinerary" ? (
              <>
                {/* HTML Template — stored in Mongo + mirrored to S3 */}
                <div>
                  <label className={labelCls}>
                    HTML Template File <span className="text-red-400">*</span>
                    <span className="text-gray-500 ml-1 font-normal">(Jinja2 .html layout — loads into the editor below)</span>
                  </label>
                  <input
                    type="file"
                    accept=".html,text/html"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      setHtmlFile(f);
                      if (f) void f.text().then((t) => setForm((prev) => ({ ...prev, html_template_content: t })));
                    }}
                    className="text-xs text-gray-400"
                  />
                  {htmlFile && <p className="text-xs text-emerald-400 mt-1">✓ {htmlFile.name}</p>}
                  {isEdit && !htmlFile && <p className="text-xs text-gray-500 mt-1">Leave blank to keep existing HTML, or edit it directly below.</p>}
                </div>

                {/* Layout HTML editor — primary render source (stored in MongoDB) */}
                <div>
                  <label className={labelCls}>
                    Layout HTML (stored in MongoDB)
                    <span className="text-gray-500 ml-1 font-normal">
                      {form.html_template_content
                        ? ` — ${form.html_template_content.length.toLocaleString()} chars`
                        : " (blank → bespoke_luxe fallback)"}
                    </span>
                  </label>
                  <textarea
                    rows={12}
                    spellCheck={false}
                    value={form.html_template_content}
                    onChange={(e) => setForm((f) => ({ ...f, html_template_content: e.target.value }))}
                    placeholder={"<!DOCTYPE html>\n<html>… your Jinja2 itinerary layout …</html>"}
                    className={`${inputCls} font-mono text-xs whitespace-pre`}
                  />
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => void handleExtractHtml()}
                      disabled={extracting || !form.html_template_content.trim()}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-xs font-medium"
                    >
                      {extracting ? "Extracting…" : "Extract fields from HTML"}
                    </button>
                    <span className="text-xs text-gray-500">Reads the template's variables → fills base prompt, prompt template &amp; fields below.</span>
                  </div>
                  {lintWarnings.length > 0 && (
                    <ul className="mt-2 space-y-1 rounded-lg border border-amber-700/40 bg-amber-900/10 p-2">
                      {lintWarnings.map((w, i) => (
                        <li key={i} className="text-xs text-amber-300">⚠ {w}</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-3">
                    <label className={labelCls}>
                      Render engine
                      <span className="text-gray-500 ml-1 font-normal">— v2 maps standardized data onto any layout (incl. per-day schedules)</span>
                    </label>
                    <select
                      value={form.render_engine}
                      onChange={(e) => setForm((f) => ({ ...f, render_engine: e.target.value }))}
                      className={inputCls}
                    >
                      <option value="v2">v2 — HTML-driven contract (default)</option>
                      <option value="legacy">legacy (fallback)</option>
                    </select>
                  </div>
                </div>

                {/* Preview source: sample PDF (easiest) */}
                <div>
                  <label className={labelCls}>
                    Preview PDF
                    <span className="text-gray-500 ml-1 font-normal">— first page becomes the thumbnail (easiest). Fields still come from the HTML above.</span>
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setSamplePdf(e.target.files?.[0] ?? null)}
                    className="text-xs text-gray-400"
                  />
                  {samplePdf && <p className="text-xs text-emerald-400 mt-1">✓ {samplePdf.name} — first page used as preview</p>}
                </div>

                {/* Optional manual preview override */}
                <div>
                  <label className={labelCls}>
                    Override Preview Image
                    <span className="text-gray-500 ml-1 font-normal">(optional — or auto-rendered from your HTML if no PDF/image given)</span>
                  </label>
                  {existingPreview && !previewFile && (
                    <img src={existingPreview} alt="preview" className="w-24 h-24 object-cover rounded-lg border border-gray-700 mb-2" />
                  )}
                  <input type="file" accept="image/*" onChange={(e) => setPreviewFile(e.target.files?.[0] ?? null)} className="text-xs text-gray-400" />
                  {prefillPreviewS3 && !previewFile && <p className="text-xs text-emerald-400 mt-1">Reusing source asset image (no re-upload needed).</p>}
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className={labelCls}>Preview Image {existingPreview ? "(keeps existing if empty)" : "(optional)"}</label>
                  {existingPreview && !previewFile && (
                    <img src={existingPreview} alt="preview" className="w-24 h-24 object-cover rounded-lg border border-gray-700 mb-2" />
                  )}
                  <input type="file" accept="image/*" onChange={(e) => setPreviewFile(e.target.files?.[0] ?? null)} className="text-xs text-gray-400" />
                  {prefillPreviewS3 && !previewFile && <p className="text-xs text-emerald-400 mt-1">Reusing source asset image (no re-upload needed).</p>}
                </div>
                {assetType === "video" && (
                  <div>
                    <label className={labelCls}>Video File (optional) <span className="text-gray-500 font-normal">— or use Generate Preview above</span></label>
                    {videoFile ? (
                      <video src={URL.createObjectURL(videoFile)} controls className="h-40 rounded-lg border border-gray-700 mb-2 block" />
                    ) : generatedVideoUrl ? (
                      <>
                        <video src={generatedVideoUrl} controls className="h-40 rounded-lg border border-gray-700 mb-2 block" />
                        <p className="text-xs text-emerald-400 mb-2">✓ Generated preview ready — will be used on save.</p>
                      </>
                    ) : null}
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        setVideoFile(f);
                        if (f) { setGeneratedVideoS3Key(null); setGeneratedVideoUrl(null); setGeneratedPreviewS3Key(null); }
                      }}
                      className="text-xs text-gray-400"
                    />
                    {prefillVideoS3 && !videoFile && !generatedVideoUrl && <p className="text-xs text-emerald-400 mt-1">Reusing source asset video (no re-upload needed).</p>}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm">Cancel</button>
            <button type="submit" disabled={saving || extracting} className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
              {extracting ? "Extracting PDF…" : saving ? "Saving…" : isEdit ? "Update Template" : "Create Template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
